/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component } from '@angular/core';
import { TherapistService } from '../../../services/therapist.service';
import { AnyoTableComponent } from 'projects/app-core/src/app/models/AnyoTableComponent';
import { TherapistFeedbackGridResponseData } from '../../../models/therapist/therapistFeedbackGridResponse';
import {
  TherapistFeedbackFilter,
  TherapistFeedbackGridRequest,
  TherapistFeedbackSearch,
} from '../../../models/therapist/therapistFeedbackGridRequest';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { UtilsService } from 'projects/app-core/src/service/utils.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import { AnyoSortDirection } from 'projects/app-core/src/models/gridRequest';
import * as moment from 'moment';
import { lastValueFrom } from 'rxjs';
import { FileUtilsService } from 'projects/app-core/src/service/fileService';

interface CheckBoxOptions {
  label: string;
  value: string;
  checked: boolean;
}

interface TherapistFeedbackExportData {
  therapistName: string;
  vertical: string;
  concern: string;
  rating: number;
  planName: string;
  userName: string;
  createdAt: string;
  partnerName: string;
  parnerId: string;
}

@Component({
  selector: 'app-therapist-feedback',
  templateUrl: './therapist-feedback.component.html',
  styleUrl: './therapist-feedback.component.scss',
})
export class TherapistFeedbackComponent extends AnyoTableComponent<
  TherapistFeedbackGridResponseData,
  TherapistFeedbackGridRequest
> {
  planCheckBoxOptions: CheckBoxOptions[] = [];
  verticalCheckBoxOptions: CheckBoxOptions[] = [];
  concernCheckBoxOptions: CheckBoxOptions[] = [];
  therapistCheckBoxOptions: CheckBoxOptions[] = [];

  verticals: CheckBoxOptions[] = [];
  concerns: CheckBoxOptions[] = [];

  globalSearchInput = '';
  partnerNameColumnSearch = '';
  dateFormat = 'dd/MM/yyyy';
  dateRange: Date[] | undefined;

  disabledDate = (current: Date) => {
    return moment(current).isBefore(moment('01/10/2023', 'dd/MM/YYYY'));
  };

  async dateChange(_: Date[]) {
    if (this.dateRange && this.dateRange.length == 2) {
      this.gridRequest!.from = Number(moment(this.dateRange[0]).format('x'));
      this.gridRequest!.to = Number(moment(this.dateRange[1]).format('x'));
    }
    await this.getData();
  }

  partnerEmailSearchFn() {
    this.partnerNameColumnSearch = this.partnerNameColumnSearch.trim();
    if (this.partnerNameColumnSearch.length) {
      if (!this.gridRequest!.searchQuery) {
        this.gridRequest!.searchQuery = {
          [TherapistFeedbackSearch.PARTNER]: '',
        };
      }
      this.gridRequest!.searchQuery!.PARTNER = this.partnerNameColumnSearch;
      this.getData();
    }
  }

  objectKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }

  sortTherapistRating(sort: string | null) {
    this.gridRequest!.sort = {
      RATING: undefined,
      CREATED_AT: undefined,
    };
    if (sort) {
      this.gridRequest!.sort!.RATING =
        sort === 'ascend' ? AnyoSortDirection.ASC : AnyoSortDirection.DESC;
    } else {
      this.gridRequest!.sort!.RATING = AnyoSortDirection.ASC;
    }
    this.getData();
  }

  sortCreatedAt(sort: string | null) {
    this.gridRequest!.sort = {
      RATING: undefined,
      CREATED_AT: undefined,
    };
    if (sort) {
      this.gridRequest!.sort!.CREATED_AT =
        sort === 'ascend' ? AnyoSortDirection.ASC : AnyoSortDirection.DESC;
    } else {
      this.gridRequest!.sort!.CREATED_AT = AnyoSortDirection.DESC;
    }
    this.getData();
  }

  constructor(
    protected override auth: AuthService,
    protected toast: ToastService,
    private therapist: TherapistService,
    public utils: UtilsService,
    private fileUtil: FileUtilsService,
  ) {
    super(auth, toast);
  }
  override async getData(): Promise<void> {
    this.pageLoading = true;
    if (!this.gridRequest.filters) {
      this.gridRequest.filters = {
        TEST: [this.testTherapistDisplay.toString()],
        PLAN_ID: undefined,
        VERTICAL_ID: undefined,
        CONCERN_ID: undefined,
      };
      this.gridRequest.sort = {
        RATING: undefined,
        CREATED_AT: AnyoSortDirection.DESC,
      };
    }
    this.gridRequest.filters.TEST = [this.testTherapistDisplay.toString()];

    this.therapist.getTherapistFeedbackGrid(this.gridRequest).subscribe({
      next: (value) => {
        this.planCheckBoxOptions = value.filterOptions.plans.map((stat) => {
          if (this.gridRequest.filters?.PLAN_ID?.includes(stat)) {
            return {
              label: stat,
              value: stat,
              checked: true,
            };
          } else {
            return {
              label: stat,
              value: stat,
              checked: false,
            };
          }
        });
        this.verticalCheckBoxOptions = value.filterOptions.verticals.map(
          (stat) => {
            if (this.gridRequest.filters?.VERTICAL_ID?.includes(stat)) {
              return {
                label: stat,
                value: stat,
                checked: true,
              };
            } else {
              return {
                label: stat,
                value: stat,
                checked: false,
              };
            }
          },
        );
        this.concernCheckBoxOptions = value.filterOptions.concerns.map(
          (stat) => {
            if (this.gridRequest.filters?.CONCERN_ID?.includes(stat)) {
              return {
                label: stat,
                value: stat,
                checked: true,
              };
            } else {
              return {
                label: stat,
                value: stat,
                checked: false,
              };
            }
          },
        );
        this.therapistCheckBoxOptions = value.filterOptions.therapists.map(
          (stat) => {
            if (this.gridRequest.filters?.THERAPIST_ID?.includes(stat)) {
              return {
                label: stat,
                value: stat,
                checked: true,
              };
            } else {
              return {
                label: stat,
                value: stat,
                checked: false,
              };
            }
          },
        );
        this.data = value.data;
        this.totalCount = value.totalRecords;
        this.pageLoading = false;
      },
      error: (err) => {
        this.pageLoading = false;
        const errorBody = err.error as IAnyoError;
        this.toast.showAnyoErrorToast(errorBody.description);
      },
    });
  }

  planFilter(plan: string[]) {
    if (plan && plan.length) {
      if (!this.gridRequest!.filters) {
        this.gridRequest!.filters = {
          [TherapistFeedbackFilter.CONCERN_ID]: [],
          [TherapistFeedbackFilter.PLAN_ID]: [],
          [TherapistFeedbackFilter.VERTICAL_ID]: [],
          [TherapistFeedbackFilter.TEST]: [],
          [TherapistFeedbackFilter.THERAPIST_ID]: [],
        };
      }
      this.gridRequest!.filters.PLAN_ID = plan;
    }
  }

  therapistFilter(plan: string[]) {
    if (plan && plan.length) {
      if (!this.gridRequest!.filters) {
        this.gridRequest!.filters = {
          [TherapistFeedbackFilter.CONCERN_ID]: [],
          [TherapistFeedbackFilter.PLAN_ID]: [],
          [TherapistFeedbackFilter.VERTICAL_ID]: [],
          [TherapistFeedbackFilter.TEST]: [],
          [TherapistFeedbackFilter.THERAPIST_ID]: [],
        };
      }
      this.gridRequest!.filters.THERAPIST_ID = plan;
    }
  }

  verticalFilter(vertical: string[]) {
    if (vertical && vertical.length) {
      if (!this.gridRequest!.filters) {
        this.gridRequest!.filters = {
          [TherapistFeedbackFilter.CONCERN_ID]: [],
          [TherapistFeedbackFilter.PLAN_ID]: [],
          [TherapistFeedbackFilter.VERTICAL_ID]: [],
          [TherapistFeedbackFilter.TEST]: [],
          [TherapistFeedbackFilter.THERAPIST_ID]: [],
        };
      }
      this.gridRequest!.filters.VERTICAL_ID = vertical;
    }
  }

  concernFilter(concern: string[]) {
    if (concern && concern.length) {
      if (!this.gridRequest!.filters) {
        this.gridRequest!.filters = {
          [TherapistFeedbackFilter.CONCERN_ID]: [],
          [TherapistFeedbackFilter.PLAN_ID]: undefined,
          [TherapistFeedbackFilter.VERTICAL_ID]: undefined,
          [TherapistFeedbackFilter.TEST]: undefined,
          [TherapistFeedbackFilter.THERAPIST_ID]: undefined,
        };
      }
      this.gridRequest!.filters.CONCERN_ID = concern;
    }
  }

  searchGlobal() {
    if (this.globalSearchInput.length) {
      this.gridRequest.globalSearch = [];
      this.gridRequest?.globalSearch?.push(this.globalSearchInput.trim());
      this.getData();
    }
  }
  testTherapistDisplay = false;
  testTherapistChangeFn($event: boolean) {
    this.testTherapistDisplay = $event;
    this.currentPageNo = 1;
    this.gridRequest = {
      page: 1,
      pageSize: this.pageSize,
      export: false,
      filters: {
        TEST: [$event.toString()],
        PLAN_ID: undefined,
        VERTICAL_ID: undefined,
        CONCERN_ID: undefined,
        THERAPIST_ID: undefined,
      },
    };
    this.getData();
  }

  override async resetFilter(): Promise<void> {
    this.gridRequest = {
      dateFilters: undefined,
      export: false,
      filters: undefined,
      from: undefined,
      globalSearch: [],
      searchQuery: undefined,
      sort: undefined,
      to: undefined,
      page: 1,
      pageSize: this.pageSize,
    };
    this.dateRange = [];
    this.globalSearchInput = '';
    this.partnerNameColumnSearch = '';
    this.getData();
  }

  async exportDataFn() {
    this.pageLoading = true;
    this.gridRequest.export = true;
    const gridResp = await lastValueFrom(
      this.therapist.getTherapistFeedbackGrid(this.gridRequest!),
    );
    const exportData = gridResp.data.map((value) => {
      const payload: TherapistFeedbackExportData = {
        createdAt: moment(value.createdAt, 'x').format('DD/MM/YYYY'),
        therapistName: value.therapistName,
        vertical: value.vertical,
        concern: value.concern,
        rating: value.rating,
        planName: value.planName,
        userName: value.userName,
        partnerName: value.partnerName,
        parnerId: value.parnerId,
      };
      return payload;
    });
    try {
      this.fileUtil.exportCSV<TherapistFeedbackExportData>(
        exportData,
        `therapist_ratings_export_${moment().format('dd/mm/yyy hh:mm a')}`,
      );
      this.pageLoading = false;
    } catch (e) {
      this.toastService.showError('Export Error');
      this.pageLoading = false;
    }
  }
}
